import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import { useTranslator } from 'tradera/lang/translate';
import { ScrollableModal } from 'tradera/components/scrollable-modal/scrollable-modal';
import { Button } from 'tradera/components/atoms/button/button';
import { showSuccessToast } from 'tradera/services/toasts';
import { buildShareObject } from './helper';
import { useAppSelector } from 'tradera/state/hooks';
import { SERVER_FETCHED_NUMBER_WISHLIST_ITEMS } from 'tradera/apps/tradera/start-page/components/wish-list/constants';
import type { WishListItemCardViewModel } from 'tradera/state/services/types/webapi-member-generated';
import { useWishListData } from 'tradera/apps/tradera/components/wish-list-side-sheet/wish-list-items';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { WishListShareItem } from './wish-list-share-item';
import styles from './wish-list-share.module.scss';

export const GA_EVENT_CATEGORY_WISHLIST_SHARE = 'Wishlist Share';

const useSelectable = () => {
    const [selectedItems, setSelectedItems] = useState<
        Array<WishListItemCardViewModel>
    >([]);
    const toggleItem = useCallback(
        (item: WishListItemCardViewModel) => {
            setSelectedItems(() => {
                return selectedItems.includes(item)
                    ? selectedItems.filter((x) => x.itemId !== item.itemId)
                    : [...selectedItems, item];
            });
        },
        [selectedItems],
    );

    return {
        selectedItems,
        toggleItem,
    };
};

type WishListShareProps = {
    onClose: () => void;
};

export const WishListShare = ({ onClose }: WishListShareProps) => {
    const { t } = useTranslator();
    const environment = useAppSelector((state) => state.environment);

    const { isMobileDevice } = environment;

    const { wishListItems, isFetching, isAllItemsFetched, page, nextPage } =
        useWishListData({ pageSize: SERVER_FETCHED_NUMBER_WISHLIST_ITEMS });

    const { ref: loadingMoreRef, inView } = useInView();

    const { selectedItems, toggleItem } = useSelectable();

    useEffect(() => {
        if (isFetching || !inView || isAllItemsFetched) return;

        nextPage();
    }, [inView, isFetching, isAllItemsFetched, nextPage]);

    const canShare =
        isMobileDevice &&
        navigator.canShare?.({
            text: 'test can share',
        });

    const handleShare = () => {
        const shareText = buildShareObject(
            selectedItems,
            t('shareModal_wishlist_message'),
        );
        if (canShare) {
            return navigator
                .share({ text: shareText })
                .then(() => {
                    GtmService.trackAction(
                        GA_EVENT_CATEGORY_WISHLIST_SHARE,
                        'Native share',
                    );
                    return;
                })
                .catch(() => {
                    // Do nothing
                });
        } else {
            return navigator.clipboard
                .writeText(shareText)
                .then(() => {
                    GtmService.trackAction(
                        GA_EVENT_CATEGORY_WISHLIST_SHARE,
                        'Copy links',
                    );
                    showSuccessToast('shareModal_wishlist_links_copied');
                    onClose();
                    return;
                })
                .catch(() => {
                    // Do nothing
                });
        }
    };

    return (
        <ScrollableModal
            maxWidth={'45rem'}
            maxHeight={'38rem'}
            onClose={onClose}
            mobileBottomSheet
            bottomSheetFullHeight
            title={t('shareModal_wishlist_title')}
            footer={
                <Button
                    onClick={handleShare}
                    disabled={selectedItems.length === 0}>
                    {canShare
                        ? t('shareModal_wishlist_share_via')
                        : t('shareModal_wishlist_copy_links')}
                </Button>
            }
            show>
            {isFetching && page === 1 ? (
                <div
                    className="d-flex text-center py-2"
                    style={{ minHeight: '28rem' }}>
                    <LoadingIcon className="icon icon-xl icon-spin m-auto" />
                </div>
            ) : (
                <>
                    <div className={styles.wishListShare}>
                        {wishListItems?.map((item) => (
                            <WishListShareItem
                                key={item.itemId}
                                item={item}
                                isSelected={selectedItems.includes(item)}
                                toggleItem={toggleItem}
                            />
                        ))}
                        {!isFetching && <div ref={loadingMoreRef} />}
                    </div>
                    {isFetching && page > 1 && (
                        <div className="text-center py-2">
                            <LoadingIcon className="icon icon-lg icon-spin m-auto" />
                        </div>
                    )}
                </>
            )}
        </ScrollableModal>
    );
};
